import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Badge as BadgeIcon,
  DocumentScanner as DocumentScannerIcon,
  Event as EventIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  LocationOn as LocationOnIcon,
  Mail as MailIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  QrCode2 as QrCode2Icon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api/axiosConfig";
import { endpoints } from "../../api/endpoints";
import UserProfileModal from "../../components/Models/UserProfileModal";
import { colors } from "../../theme/colors";
import { getApiErrorMessage } from "../../utils/apiError";

type StationStatus = "Active" | "Inactive";

type ManagerDetail = {
  id: string;
  name: string;
  email: string;
  phone: string;
  joinedAt: string;
};

type StaffMember = {
  id: string;
  name: string;
  phone: string;
  email: string;
  status: "Active" | "Inactive" | "On Leave";
  joinDate: string;
  shift: string;
};

type StationDetail = {
  id: string;
  stationId: string;
  name: string;
  stationType: string;
  status: StationStatus;
  manager: ManagerDetail;
  city: string;
  state: string;
  pinCode: string;
  branchName: string;
  address: string;
  totalActiveStaff: number;
  totalScans: number;
  totalQrStickersApplied: number;
  staff: {
    items: StaffMember[];
    total: number;
    page: number;
    pageSize: number;
    pages: number;
  };
};

const normalizeStationTypeLabel = (value?: string) => {
  if (!value) {
    return "Fuel Station";
  }

  if (value === "FUEL_STATION" || value === "Fuel Station") {
    return "Fuel Station";
  }

  return "Testing Centre";
};

const normalizeStatus = (value: unknown): StationStatus => {
  if (typeof value === "boolean") {
    return value ? "Active" : "Inactive";
  }

  if (typeof value === "string") {
    return value.toLowerCase() === "active" ? "Active" : "Inactive";
  }

  return "Inactive";
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "--";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return "--";
  }
};

const normalizeStaffStatus = (value?: string): StaffMember["status"] => {
  switch ((value || "").toLowerCase()) {
    case "active":
      return "Active";
    case "on_leave":
    case "on leave":
      return "On Leave";
    default:
      return "Inactive";
  }
};

const pageCardSx = {
  borderRadius: "8px",
  border: "1px solid #E5E7EB",
  boxShadow: "0 2px 6px rgba(15, 23, 42, 0.08)",
  overflow: "hidden",
};

const getStaffStatusStyles = (status: StaffMember["status"]) => {
  if (status === "Active") {
    return {
      bgcolor: "#E8F5E9",
      color: "#2E7D32",
    };
  }

  if (status === "On Leave") {
    return {
      bgcolor: "#FDECEC",
      color: "#D32F2F",
    };
  }

  return {
    bgcolor: "#FFF3E0",
    color: "#EF6C00",
  };
};

const mapApiStation = (item: any): StationDetail => {
  console.log("API Station Item:", item);
  const staffItems: StaffMember[] = Array.isArray(item?.staff?.items)
    ? item.staff.items.map((staffItem: any) => ({
        id: staffItem?.public_user_id || staffItem?.id || "--",
        name: staffItem?.name || "--",
        phone: staffItem?.phone || "--",
        email: staffItem?.email || "--",
        status: normalizeStaffStatus(staffItem?.status),
        joinDate: formatDate(staffItem?.joined_at || ""),
        shift: "--",
      }))
    : [];

  return {
    id: item?.id || "",
    stationId: item?.station_id || item?.id || "--",
    name: item?.name || "Unnamed Station",
    stationType: normalizeStationTypeLabel(item?.station_type || item?.type),
    status: normalizeStatus(item?.is_active ?? item?.status),
    manager: {
      id: item?.manager?.public_user_id || item?.manager?.id || "--",
      name: item?.manager?.name || "--",
      email: item?.manager?.email || "--",
      phone: item?.manager?.phone || "--",
      joinedAt: formatDate(item?.manager?.joined_at || ""),
    },
    city: item?.city || "--",
    state: item?.state || "--",
    pinCode: item?.pincode || "--",
    branchName: item?.cgd_name || "--",
    address: item?.address || "--",
    totalActiveStaff: item?.total_active_staff || 0,
    totalScans: item?.total_scans || 0,
    totalQrStickersApplied: item?.total_qr_stickers_applied || 0,
    staff: {
      items: staffItems,
      total: item?.staff?.total || staffItems.length,
      page: item?.staff?.page || 1,
      pageSize: item?.staff?.page_size || 20,
      pages:
        item?.staff?.pages ||
        Math.ceil(
          (item?.staff?.total || staffItems.length) /
            (item?.staff?.page_size || 20),
        ),
    },
  };
};

const StationDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { stationId } = useParams();

  const [station, setStation] = useState<StationDetail | null>(null);
  const [staffPage, setStaffPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const activeRequestIdRef = useRef(0);

  useEffect(() => {
    if (!stationId) {
      setError("Station ID is missing");
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const requestId = activeRequestIdRef.current + 1;

    activeRequestIdRef.current = requestId;

    const fetchStation = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get(endpoints.stations.getById(stationId), {
          params: {
            staff_page: staffPage,
            staff_page_size: 20,
          },
          signal: controller.signal,
        });

        setStation(mapApiStation(response.data));
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }

        const message = getApiErrorMessage(
          err,
          "Failed to load station details.",
        );

        setError(message);
      } finally {
        if (activeRequestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    };

    fetchStation();

    return () => {
      controller.abort();
    };
  }, [stationId, staffPage]);

  const metrics = useMemo(
    () => [
      {
        label: "Total Staff",
        value: station?.totalActiveStaff?.toString() || "0",
        color: "#2E7D32",
        icon: <PeopleIcon sx={{ color: "#2E7D32", fontSize: 20 }} />,
        iconBg: "#E8F5E9",
      },
      {
        label: "Total Scans",
        value: station?.totalScans?.toString() || "0",
        color: "#EF6C00",
        icon: <DocumentScannerIcon sx={{ color: "#EF6C00", fontSize: 20 }} />,
        iconBg: "#FFF3E0",
      },
      {
        label: "QR Stickers Issued",
        value: station?.totalQrStickersApplied?.toString() || "0",
        color: "#1E88E5",
        icon: <QrCode2Icon sx={{ color: "#1E88E5", fontSize: 20 }} />,
        iconBg: "#E3F2FD",
      },
    ],
    [
      station?.totalActiveStaff,
      station?.totalQrStickersApplied,
      station?.totalScans,
    ],
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, m: 2 }}>
        <Typography sx={{ color: "#D32F2F" }}>{error}</Typography>
      </Paper>
    );
  }

  if (!station) {
    return (
      <Paper sx={{ p: 3, m: 2 }}>
        <Typography>Station details are unavailable.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ bgcolor: "#F5F5F5", minHeight: "100vh" }}>
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.25,
          bgcolor: colors.white,
          borderBottom: "1px solid #E5E7EB",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}
          >
            <Box
              onClick={() => navigate("/dashboard/station-onboarding")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: colors.textGray,
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 14 }} />
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: colors.textGray,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Back
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "24px",
                lineHeight: 1,
                fontWeight: 700,
                color: colors.black,
                fontFamily: "'Inter', sans-serif",
              }}
            >
              {station.name} Dashboard
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: "12px",
              color: colors.darkGray,
              fontWeight: 400,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Admin Console / Station Onboarding / {station.name}
          </Typography>
        </Box>

        <UserProfileModal />
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
        <Paper sx={{ ...pageCardSx, p: 2.25, mb: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 2, md: 5 },
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  fontSize: "11px",
                  color: "#999999",
                  textTransform: "uppercase",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                <Inventory2OutlinedIcon
                  sx={{ fontSize: "11px", color: colors.primary }}
                />
                Station Information
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: colors.black,
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 400,
                  mb: 0.6,
                }}
              >
                <strong>Type:</strong> {station.stationType}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: colors.black,
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 400,
                  mb: 0.6,
                }}
              >
                <strong>CGD:</strong> {station.branchName}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: colors.black,
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 400,
                }}
              >
                <strong>ID:</strong> {station.stationId}
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  fontSize: "11px",
                  color: "#999999",
                  textTransform: "uppercase",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                <LocationOnIcon
                  sx={{ fontSize: "11px", color: colors.primary }}
                />
                Address
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#232323",
                  fontWeight: 400,
                  fontFamily: "'Inter', sans-serif",
                  mb: 0.6,
                }}
              >
                {station.address}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#232323",
                  fontWeight: 400,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {station.city}, {station.state} - {station.pinCode}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          {metrics.map((metric) => (
            <Grid key={metric.label} size={{ xs: 12, md: 4 }}>
              <Paper
                sx={{
                  ...pageCardSx,
                  p: 1.75,
                  borderLeft: `3px solid ${metric.color}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "6px",
                      bgcolor: metric.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {metric.icon}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "34px",
                        fontFamily: "'Inter', sans-serif",
                        lineHeight: 1,
                        fontWeight: 700,
                        color: colors.black,
                        mb: 0.5,
                      }}
                    >
                      {metric.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: colors.textGray,
                        fontWeight: 400,
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      {metric.label}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper sx={{ ...pageCardSx, mb: 1.5 }}>
          <Box sx={{ px: 2.25, py: 1.5, borderBottom: "1px solid #ECECEC" }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "16px",
                color: colors.black,
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Station Manager Details
            </Typography>
          </Box>

          <Box
            sx={{
              px: 2.5,
              py: 2.25,
              display: "flex",
              gap: { xs: 2, md: 3 },
              alignItems: "flex-start",
              flexWrap: "wrap",
              minHeight: 130,
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", md: 130 },
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "flex-start", md: "center" },
                gap: 1.1,
                pt: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: colors.primary,
                  color: colors.white,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "36px",
                  fontWeight: 700,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {(station.manager?.name || "S").charAt(0).toUpperCase()}
              </Box>
              <Box sx={{ textAlign: { xs: "left", md: "center" } }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "18px",
                    fontFamily: "'Inter', sans-serif",
                    color: colors.black,
                    lineHeight: 1.2,
                  }}
                >
                  {station.manager?.name || "--"}
                </Typography>
                <Chip
                  label="Station Manager"
                  size="small"
                  sx={{
                    mt: 0.65,
                    bgcolor: "#E8F5E9",
                    color: colors.primary,
                    height: 16,
                    fontSize: "8px",
                    fontWeight: 600,
                    borderRadius: "4px",
                    fontFamily: "'Inter', sans-serif",
                    "& .MuiChip-label": {
                      px: 0.75,
                    },
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(140px, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                },
                columnGap: 3,
                rowGap: 1.75,
                flex: 1,
                minWidth: 280,
                pt: 0.5,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: colors.textGrayLight,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "'Inter', sans-serif",
                    gap: 0.45,
                    mb: 0.55,
                  }}
                >
                  <BadgeIcon
                    sx={{
                      fontSize: 16,
                      color: colors.primary,
                      fontFamily: "'Inter', sans-serif",
                    }}
                  />{" "}
                  Employee ID
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: colors.black,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {station.manager?.id || "--"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: colors.textGrayLight,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.45,
                    mb: 0.55,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  <EventIcon sx={{ fontSize: 16, color: colors.primary }} />{" "}
                  Join Date
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: colors.black,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {station.manager?.joinedAt || "--"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: colors.textGrayLight,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.45,
                    mb: 0.55,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  <MailIcon sx={{ fontSize: 16, color: colors.primary }} />{" "}
                  Email
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: colors.black,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {station.manager?.email || "--"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: colors.textGrayLight,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.45,
                    mb: 0.55,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  <PhoneIcon sx={{ fontSize: 16, color: colors.primary }} />{" "}
                  Phone
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: colors.black,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  +91 {station.manager?.phone || "--"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ ...pageCardSx }}>
          <Box sx={{ px: 2.25, py: 1.5, borderBottom: "1px solid #ECECEC" }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "16px",
                color: colors.black,
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Fillers Details
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#FAFAFA" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: colors.black,
                      borderBottom: "1px solid #ECECEC",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: colors.black,
                      borderBottom: "1px solid #ECECEC",
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: colors.black,
                      borderBottom: "1px solid #ECECEC",
                    }}
                  >
                    Phone
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: colors.black,
                      borderBottom: "1px solid #ECECEC",
                    }}
                  >
                    Email
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: colors.black,
                      borderBottom: "1px solid #ECECEC",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: colors.black,
                      borderBottom: "1px solid #ECECEC",
                    }}
                  >
                    Join Date
                  </TableCell>
                  {/* <TableCell
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: colors.black,
                      borderBottom: "1px solid #ECECEC",
                    }}
                  >
                    Shift
                  </TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {station?.staff?.items && station.staff.items.length > 0 ? (
                  station.staff.items.map((filler) => (
                    <TableRow
                      key={filler.id}
                      sx={{ "& td": { borderBottom: "1px solid #F1F5F9" } }}
                    >
                      <TableCell
                        sx={{
                          fontSize: "14px",
                          color: colors.black,
                          fontWeight: 400,
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {filler.id}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "14px",
                          color: colors.black,
                          fontWeight: 400,
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {filler.name}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "14px",
                          color: colors.black,
                          fontWeight: 400,
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {filler.phone}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "14px",
                          color: colors.black,
                          fontWeight: 400,
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {filler.email}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={filler.status}
                          size="small"
                          sx={{
                            ...getStaffStatusStyles(filler.status),
                            height: 18,
                            fontSize: "9px",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "11px", color: "#444444" }}>
                        {filler.joinDate}
                      </TableCell>
                      {/* <TableCell sx={{ fontSize: "11px", color: "#444444" }}>
                        {filler.shift}
                      </TableCell> */}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" sx={{ color: "#999" }}>
                        No staff data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {station?.staff && station.staff.pages > 1 && (
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                borderTop: "1px solid #F1F5F9",
              }}
            >
              <Typography sx={{ color: "#666666", fontSize: "14px" }}>
                Showing {station.staff.items.length} of {station.staff.total}{" "}
                staff
              </Typography>
              <Pagination
                count={station.staff.pages}
                page={staffPage}
                onChange={(e, value) => setStaffPage(value)}
                color="primary"
                shape="rounded"
              />
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default StationDashboardPage;
